<script setup lang="ts">
import { computed } from 'vue';
import Avatar from 'primevue/avatar';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import BuildingIcon from '@primeicons/vue/building';
import DollarIcon from '@primeicons/vue/dollar';
import UserPlusIcon from '@primeicons/vue/user-plus';
import ExclamationTriangleIcon from '@primeicons/vue/exclamation-triangle';
import { Column, Columns, Stack, Typography } from '@apparatix/library/core';
import AccountLabelTag from '@apparatix/leads/AccountLabelTag.vue';
import ContactTypeChip from '@apparatix/leads/ContactTypeChip.vue';
import type { MergeAccountAssociation, MergeAccountSummary, MergePreview } from '@apparatix/types/Contacts';
import type { MergeSummary } from './mergeWizardHelpers';

type Props = {
  preview: MergePreview;
  summary: MergeSummary;
  newPrimaryBillingAccounts: MergeAccountAssociation[];
  sourceBillingChangeAccounts: MergeAccountSummary[];
}

const props = defineProps<Props>();

const hasValue = (value?: string | null): boolean => Boolean(value && String(value).trim() !== '');

const summaryFields = computed(() => props.summary.fields.filter(field => hasValue(field.value)));

const hasAssociations = computed(() => props.preview.associations.accounts.length > 0 || props.preview.associations.campaigns.length > 0);
</script>
<template>
  <Stack :gap="5">
    <Message
      v-for="account in props.newPrimaryBillingAccounts"
      :key="`new-primary-billing-${account.c2Id}`"
      severity="warn"
    >
      <template #icon>
        <ExclamationTriangleIcon />
      </template>
      <span class="font-bold">{{ props.summary.fullName }}</span> will become the bill recipient for active contracts on
      <span class="font-bold">{{ account.c2Name }}</span>.
    </Message>
    <Message
      v-for="account in props.sourceBillingChangeAccounts"
      :key="`source-billing-change-${account.c2Id}`"
      severity="warn"
    >
      <template #icon>
        <ExclamationTriangleIcon />
      </template>
      <span class="font-bold">{{ props.summary.fullName }}</span> is the bill recipient for active contracts on
      <span class="font-bold">{{ account.c2Name }}</span> &mdash; its name, address, email, or phone number is changing.
    </Message>
    <Columns class="lg:gap-x-8">
      <Column>
        <Stack :gap="4">
          <Typography variant="section-heading">
            Contact after combining
          </Typography>
          <div class="flex flex-col gap-2 p-3 rounded-xl bg-[#F1F5F9]">
            <div v-for="field in summaryFields" :key="field.key" class="flex gap-2">
              <Typography variant="form-label">
                {{ field.label }}:
              </Typography>
              <span>{{ field.value }}</span>
            </div>
            <div v-if="hasValue(props.summary.address)" class="flex gap-2">
              <Typography variant="form-label">
                Address:
              </Typography>
              <span>{{ props.summary.address }}</span>
            </div>
            <div v-if="props.summary.emails.length" class="flex gap-2">
              <Typography variant="form-label">
                Emails:
              </Typography>
              <span>{{ props.summary.emails.map(option => option.label).join(', ') }}</span>
            </div>
            <div v-if="props.summary.phoneNumbers.length" class="flex gap-2">
              <Typography variant="form-label">
                Phone Numbers:
              </Typography>
              <span>{{ props.summary.phoneNumbers.map(option => option.label).join(', ') }}</span>
            </div>
            <div v-if="props.summary.webPages.length" class="flex gap-2">
              <Typography variant="form-label">
                Websites:
              </Typography>
              <span>{{ props.summary.webPages.map(option => option.label).join(', ') }}</span>
            </div>
            <div v-if="hasValue(props.summary.note)" class="flex gap-2">
              <Typography variant="form-label">
                Note:
              </Typography>
              <span class="whitespace-pre-line">{{ props.summary.note }}</span>
            </div>
          </div>
        </Stack>
      </Column>
      <Column>
        <Stack :gap="4">
          <Typography variant="section-heading">
            Relationships transferring from the Selected Contact
          </Typography>
          <Typography v-if="!hasAssociations" variant="body" muted>
            The Selected Contact is not attached to any accounts or campaigns.
          </Typography>
          <Stack v-if="props.preview.associations.accounts.length" :gap="2">
            <Typography variant="form-heading">
              Accounts
            </Typography>
            <div
              v-for="account in props.preview.associations.accounts"
              :key="`account-${account.c2Id}`"
              class="flex flex-col gap-2 p-3 rounded-lg border border-[var(--p-content-border-color)] bg-white"
            >
              <div class="flex items-center gap-4">
                <div class="flex-none">
                  <Avatar
                    size="large"
                    shape="circle"
                    style="background-color: var(--p-teal-100); color: var(--p-teal-500);"
                  >
                    <template #icon>
                      <BuildingIcon />
                    </template>
                  </Avatar>
                </div>
                <div class="flex flex-col gap-1 min-w-0">
                  <span class="text-sm font-medium text-[var(--swimlane-primary-text)] leading-snug">{{ account.c2Name }}</span>
                  <span v-if="account.c2Type" class="text-xs font-normal text-[var(--swimlane-secondary-text)] leading-snug">{{ account.c2Type }}</span>
                </div>
                <div class="flex-auto flex flex-wrap items-center justify-end gap-2">
                  <ContactTypeChip v-if="account.c2Type" :label="account.c2Type" />
                  <AccountLabelTag :label="account.accountLabel" />
                  <Tag v-if="account.hasActiveContracts" value="Active Contracts" :severity="account.selectedIsPrimary ? 'warn' : 'secondary'" />
                </div>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <ContactTypeChip
                  v-for="label in account.contactLabels"
                  :key="`account-${account.c2Id}-label-${label.contactLabelId}`"
                  :label="label.contactLabelDisplayName"
                />
                <Tag
                  v-if="account.sourceAlreadyMapped"
                  value="Labels will be combined onto existing link"
                  severity="info"
                />
              </div>
            </div>
          </Stack>
          <Stack v-if="props.preview.associations.campaigns.length" :gap="2">
            <Typography variant="form-heading">
              Campaigns
            </Typography>
            <div
              v-for="campaign in props.preview.associations.campaigns"
              :key="`campaign-${campaign.id}`"
              class="flex flex-col gap-2 p-3 rounded-lg border border-[var(--p-content-border-color)] bg-white"
            >
              <div class="flex items-center gap-4">
                <div class="flex-none">
                  <Avatar
                    v-if="campaign.type === 'Lead'"
                    size="large"
                    shape="circle"
                    style="background-color: var(--p-amber-100); color: var(--p-amber-500);"
                  >
                    <template #icon>
                      <UserPlusIcon />
                    </template>
                  </Avatar>
                  <Avatar
                    v-else
                    size="large"
                    shape="circle"
                    style="background-color: var(--p-lime-100); color: var(--p-lime-500);"
                  >
                    <template #icon>
                      <DollarIcon />
                    </template>
                  </Avatar>
                </div>
                <div class="flex flex-col gap-1 min-w-0">
                  <span class="text-sm font-medium text-[var(--swimlane-primary-text)] leading-snug">{{ campaign.name }}</span>
                  <span v-if="campaign.type" class="text-xs font-normal text-[var(--swimlane-secondary-text)] leading-snug">{{ campaign.type }}</span>
                </div>
                <div class="flex-auto flex flex-wrap items-center justify-end gap-2">
                  <Tag v-if="campaign.status" :value="campaign.status" :severity="campaign.isActive ? 'success' : 'secondary'" />
                </div>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <ContactTypeChip
                  v-for="label in campaign.contactLabels"
                  :key="`campaign-${campaign.id}-label-${label.contactLabelId}`"
                  :label="label.contactLabelDisplayName"
                />
                <Tag
                  v-if="campaign.sourceAlreadyMapped"
                  value="Labels will be combined onto existing link"
                  severity="info"
                />
              </div>
            </div>
          </Stack>
        </Stack>
      </Column>
    </Columns>
  </Stack>
</template>
